import { useCallback, useState, type FormEvent } from "react";
import { SessionList } from "./Sessions";
import { useLantern, useSession } from "../hooks";
import { StatusDialog } from "./AccountMenu";
import { Avatar, Badge, Card, Field, Form, Page } from "./ui";
import { browserZone, localTime, timeZones } from "../localtime";
import { locale, t, tn } from "../i18n";
import { setPhoto } from "../avatar";

// roleName is a role as people read it.
const roleName = (role: string) => ({ admin: t("admin"), owner: t("owner"), member: t("member"), guest: t("guest") })[role] ?? role;

// ProfilePage is what others see of you: your name, handle and status.
export function ProfilePage() {
  const session = useSession();
  const state = useLantern();
  const me = state.me;
  const [name, setName] = useState(me?.display_name ?? "");
  const [title, setTitle] = useState(me?.title ?? "");
  const [pronouns, setPronouns] = useState(me?.pronouns ?? "");
  const [zone, setZone] = useState(me?.time_zone || browserZone());
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [photoError, setPhotoError] = useState("");
  if (!me) return null;

  async function choosePhoto(file?: File) {
    if (!file) return;
    setPhotoError("");
    try {
      await setPhoto(session, file);
    } catch (err) {
      setPhotoError(err instanceof Error ? err.message : String(err));
    }
  }
  const status = session.statusOf(me.id);
  const presence = state.presence.get(me.id) ?? (me.away ? "away" : "active");

  async function save(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSaved(false);
    try {
      await session.setOwn(() => session.api.updateMe({ display_name: name, title, pronouns, time_zone: zone }));
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Page className="settings profile-page" narrow title={t("Profile")} description={t("What people see of you beside your messages, in mentions and in their direct messages.")}>
      <Card>
        <div className="profile-hero">
          <div className="photo-edit">
            <label className="photo-pick" title={me.avatar_at ? t("Change photo") : t("Upload a photo")}>
              <Avatar id={me.id} name={name || me.handle} size={72} presence={presence} />
              <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden tabIndex={-1} onChange={(e) => void choosePhoto(e.target.files?.[0])} />
            </label>
            <label className="link">
              {me.avatar_at ? t("Change photo") : t("Upload a photo")}
              <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={(e) => void choosePhoto(e.target.files?.[0])} aria-label={t("Profile photo")} />
            </label>
            {me.avatar_at && (
              <button className="link" onClick={() => void session.setOwn(() => session.api.removeAvatar()).catch((err) => setPhotoError(String(err)))}>
                {t("Remove photo")}
              </button>
            )}
            {photoError && <p className="error">{photoError}</p>}
          </div>
          <div>
            <h2>
              {me.display_name || me.handle}
              {me.pronouns && <span className="pronouns"> ({me.pronouns})</span>}
            </h2>
            {me.title && <p className="profile-title">{me.title}</p>}
            <p className="muted">
              @{me.handle} · {me.address}
            </p>
            <p>
              <Badge tone={presence === "away" ? "neutral" : "ok"}>{presence === "away" ? t("Away") : t("Active")}</Badge>
              {me.role && me.role !== "member" && <Badge tone="accent">{roleName(me.role)}</Badge>}
            </p>
          </div>
        </div>
      </Card>

      <Card title={t("About you")} description={t("Shown on your profile card. Your handle stays the same; people @mention you by it.")}>
        <Form onSubmit={save}>
          <Field label={t("Display name")}>
            <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label={t("Title")} hint={t("What you do, such as Staff engineer, Payments.")}>
            <input value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} placeholder={t("Your role or team")} />
          </Field>
          <div className="inline-fields">
            <Field label={t("Pronouns")}>
              <input value={pronouns} maxLength={30} onChange={(e) => setPronouns(e.target.value)} placeholder={t("she/her, he/him, they/them")} list="pronoun-options" />
              <datalist id="pronoun-options">
                <option value="she/her" />
                <option value="he/him" />
                <option value="they/them" />
              </datalist>
            </Field>
            <Field label={t("Time zone")} hint={t("People see your local time: {time} now.", { time: localTime(zone, zone)?.time ?? "" })}>
              <select value={zone} onChange={(e) => setZone(e.target.value)}>
                {timeZones(zone).map((z) => (
                  <option key={z} value={z}>
                    {z.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {error && <p className="error">{error}</p>}
          {saved && <p className="notice">{t("Saved. Everyone sees it now.")}</p>}
          <div className="form-actions">
            <button
              type="submit"
              disabled={!name.trim() || (name.trim() === me.display_name && title.trim() === (me.title ?? "") && pronouns.trim() === (me.pronouns ?? "") && zone === me.time_zone)}
            >
              {t("Save")}
            </button>
          </div>
        </Form>
      </Card>

      <Card
        title={t("Status")}
        description={t("Shown beside your name, until it clears.")}
        actions={
          <>
            {status && (
              <button className="small" onClick={() => void session.setOwn(() => session.api.setStatus("", "", null))}>
                {t("Clear")}
              </button>
            )}
            <button className="small primary" onClick={() => setStatusOpen(true)}>
              {status ? t("Change") : t("Set a status")}
            </button>
          </>
        }
      >
        {status ? (
          <p className="status-line">
            <span className="status-emoji">{status.emoji || "💬"}</span> {status.text}
            {me.status_expires_at && (
              <span className="muted">
                {" · "}
                {t("clears {time}", { time: new Date(me.status_expires_at).toLocaleString(locale, { weekday: "short", hour: "numeric", minute: "2-digit" }) })}
              </span>
            )}
          </p>
        ) : (
          <p className="muted">{t("No status set.")}</p>
        )}
      </Card>
      {statusOpen && <StatusDialog onClose={() => setStatusOpen(false)} />}
    </Page>
  );
}

// AccountCards are your account, in Settings: who you sign in as, and
// your password.
export function AccountCards() {
  const session = useSession();
  const state = useLantern();
  const me = state.me;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [mismatch, setMismatch] = useState(false);
  if (!me) return null;

  async function save(e: FormEvent) {
    e.preventDefault();
    setError("");
    setDone(false);
    setMismatch(next !== again);
    if (next !== again) return;
    try {
      await session.api.changePassword(current, next);
      setDone(true);
      setCurrent("");
      setNext("");
      setAgain("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <>
      <Card title={t("Account")}>
        <dl className="facts">
          <dt>{t("Workspace")}</dt>
          <dd>{state.workspace || "—"}</dd>
          <dt>{t("Handle")}</dt>
          <dd>@{me.handle}</dd>
          <dt>{t("Address")}</dt>
          <dd>{me.address}</dd>
          {me.email && (
            <>
              <dt>{t("Email")}</dt>
              <dd>{me.email}</dd>
            </>
          )}
          <dt>{t("Role")}</dt>
          <dd>{roleName(me.role ?? "member")}</dd>
        </dl>
      </Card>

      <Card title={t("Password")} description={t("For signing in without single sign-on. Use at least 12 characters.")}>
        <Form onSubmit={save} className="password-form">
          <Field label={t("Current password")}>
            <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
          </Field>
          <div className="inline-fields">
            <Field label={t("New password")}>
              <input type="password" autoComplete="new-password" minLength={12} value={next} onChange={(e) => setNext(e.target.value)} required />
            </Field>
            <Field label={t("New password again")} error={mismatch ? t("The new passwords do not match.") : undefined}>
              <input
                type="password"
                autoComplete="new-password"
                value={again}
                onChange={(e) => {
                  setAgain(e.target.value);
                  setMismatch(false);
                }}
                required
              />
            </Field>
          </div>
          {error && <p className="error">{error}</p>}
          {done && <p className="notice">{t("Your password is changed, and your other devices are signed out.")}</p>}
          <div className="form-actions">
            <button type="submit">{t("Change password")}</button>
          </div>
        </Form>
      </Card>
    </>
  );
}

// SessionsCards are where you are signed in, in Settings, and signing
// out, here or everywhere else.
export function SessionsCards({ onSignOut }: { onSignOut: () => void }) {
  const session = useSession();
  const state = useLantern();
  const [others, setOthers] = useState("");
  const loadSessions = useCallback(() => session.api.sessions().then((r) => r.sessions), [session]);

  async function signOutOthers() {
    try {
      const { sessions } = await session.api.signOutOthers();
      setOthers(sessions ? tn(sessions, "Signed out of {n} other session.", "Signed out of {n} other sessions.") : t("You were signed in nowhere else."));
    } catch (err) {
      setOthers(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <>
      <Card title={t("Where you're signed in")} description={t("Each browser and device signed in as you. Sign out any you do not know or no longer use.")}>
        <SessionList load={loadSessions} end={(id) => session.api.endSession(id)} label={t("Where you're signed in")} key={others} />
      </Card>

      <Card
        title={t("Sign out")}
        description={
          state.workspace
            ? t("End this browser's session in {workspace}, or every other one: other browsers and devices where you are signed in.", { workspace: state.workspace })
            : t("End this browser's session in this workspace, or every other one: other browsers and devices where you are signed in.")
        }
      >
        <div className="form-actions">
          <button onClick={() => void signOutOthers()}>{t("Sign out other devices")}</button>
          <button className="danger" onClick={onSignOut}>
            {t("Sign out")}
          </button>
        </div>
        {others && <p className="notice">{others}</p>}
      </Card>
    </>
  );
}
